import { format } from 'date-fns'

import { FIREBASE_PARAM_KEYS, StoreEventName } from '@/constants/storeEvents'
import {
  CleverTapEventValue,
  pushCleverTapEvent
} from '@/utils/clevertap.utils'
import { logFirebaseEvent } from '@/utils/firebase.utils'

export type StoreEventParams = Record<
  string,
  CleverTapEventValue | null | undefined
>

/**
 * `timestamp` format the mobile apps (shared KMM layer) and the existing web
 * Firebase events use: local time, no timezone.
 */
export const eventTimestamp = (date: Date = new Date()): string =>
  format(date, 'dd/MM/yyyy - HH:mm:ss')

// Firebase (GA4) cuts a custom parameter value at 100 characters.
const FIREBASE_MAX_VALUE_LENGTH = 100

const isPresent = (value: StoreEventParams[string]) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0)

const cleanParams = (params: StoreEventParams) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => isPresent(value))
  ) as Record<string, CleverTapEventValue>

// Firebase takes scalars only. Like the mobile apps, send every value as a
// string, and join arrays.
const toFirebaseParams = (
  params: Record<string, CleverTapEventValue>,
  keyMap: Record<string, string> = {}
) =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [
      keyMap[key] ?? key,
      (Array.isArray(value) ? value.join(',') : String(value)).slice(
        0,
        FIREBASE_MAX_VALUE_LENGTH
      )
    ])
  )

/**
 * Send one business event to CleverTap and Firebase. Empty values are dropped,
 * CleverTap keeps the original types, Firebase gets strings (and the key
 * renames in FIREBASE_PARAM_KEYS). Each destination is isolated, and this never
 * throws, so analytics can never break the action that triggered it.
 */
export function trackStoreEvent(
  name: StoreEventName,
  params: StoreEventParams = {}
): void {
  const clean = cleanParams(params)

  try {
    pushCleverTapEvent(name, clean)
  } catch (error) {
    console.warn('CleverTap event failed', error)
  }

  try {
    logFirebaseEvent(name, toFirebaseParams(clean, FIREBASE_PARAM_KEYS[name]))
  } catch (error) {
    console.warn('Firebase event failed', error)
  }
}

// --- send once -------------------------------------------------------------
// Some triggers can be seen more than once: the same socket message, a reload,
// or several tabs of one store. The ledger remembers what was already sent
// (shared across tabs and reloads, expiring after a day) so each order event is
// reported once. Mobile does the same for received / auto-accepted orders.

const LEDGER_KEY = 'storeEventsLedger'
const LEDGER_LOCK = 'storeEventsLedger'
const LEDGER_TTL_MS = 24 * 60 * 60 * 1000
const LEDGER_MAX_ENTRIES = 500

type Ledger = Record<string, number>

// Per-tab memory: also dedupes when localStorage is blocked or full.
const claimedInThisTab = new Set<string>()

const readLedger = (): Ledger => {
  try {
    // A stored "null" parses fine but is not a ledger.
    return (JSON.parse(localStorage.getItem(LEDGER_KEY) ?? '{}') ??
      {}) as Ledger
  } catch {
    return {}
  }
}

const writeLedger = (ledger: Ledger) => {
  try {
    localStorage.setItem(LEDGER_KEY, JSON.stringify(ledger))
  } catch {
    // Storage blocked or full: the per-tab memory still dedupes this tab.
  }
}

// Synchronous check-and-set against this tab's memory and the shared ledger.
const claim = (key: string, now: number): boolean => {
  if (claimedInThisTab.has(key)) return false

  claimedInThisTab.add(key)
  if (claimedInThisTab.size > LEDGER_MAX_ENTRIES) {
    claimedInThisTab.delete(claimedInThisTab.values().next().value as string)
  }

  const ledger = Object.fromEntries(
    Object.entries(readLedger()).filter(([, at]) => now - at < LEDGER_TTL_MS)
  )
  if (key in ledger) return false

  ledger[key] = now
  const newest = Object.entries(ledger)
    .sort(([, a], [, b]) => a - b)
    .slice(-LEDGER_MAX_ENTRIES)
  writeLedger(Object.fromEntries(newest))

  return true
}

// How long the tab that claimed an event keeps its lock. Tabs see the same
// message within milliseconds of each other, and a localStorage write made by
// one tab is not guaranteed to be visible to another that quickly, so the
// ledger alone can let two tabs through (it did: one order was reported twice).
// A lock held for a while cannot: the other tabs are turned away at once, and
// by the time it is released the ledger is up to date everywhere.
const CLAIM_LOCK_HOLD_MS = 30_000

const claimOnce = (key: string): Promise<boolean> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (!locks) return Promise.resolve(claim(key, Date.now()))

  return new Promise(resolve => {
    locks
      .request(`${LEDGER_LOCK}:${key}`, { ifAvailable: true }, lock => {
        // No lock: another tab is reporting this event right now.
        const claimed = lock !== null && claim(key, Date.now())
        resolve(claimed)

        return claimed
          ? new Promise<void>(release =>
              setTimeout(release, CLAIM_LOCK_HOLD_MS)
            )
          : undefined
      })
      .catch(() => resolve(claim(key, Date.now())))
  })
}

/**
 * trackStoreEvent, but only the first time `dedupeId` is seen for this event
 * (across reloads and open tabs, for a day). Never throws.
 */
export async function trackStoreEventOnce(
  name: StoreEventName,
  dedupeId: string,
  params: StoreEventParams = {}
): Promise<void> {
  try {
    if (await claimOnce(`${name}:${dedupeId}`)) trackStoreEvent(name, params)
  } catch (error) {
    console.warn('Store event failed', error)
  }
}
